import type { TextAlignmentModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import { Toggle } from "@replo/design-system/components/toggle/Toggle";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import { AlignCenter, AlignJustify, AlignLeft, AlignRight } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

type ValidAlignment = "left" | "center" | "right" | "justify";

const getDisplayValue = (cssValue: string | null): string | null => {
  if (!cssValue) {
    return null;
  }
  switch (cssValue) {
    case "start":
      return "left";
    case "end":
      return "right";
    default:
      return cssValue;
  }
};

interface TextAlignmentModifierProps {
  onChange: (modifiers: TextAlignmentModifierValue[]) => void;
}

const TextAlignmentModifierComponent: React.FC<TextAlignmentModifierProps> = ({
  onChange,
}) => {
  const rawValue =
    useSemanticModifierValue({
      semanticExtractor: ({ computedStyles }) => {
        return computedStyles.textAlign || null;
      },
    }) ?? null;

  const currentValue = getDisplayValue(rawValue);

  const [alignment, setAlignment] = useOverridableState(
    currentValue ?? "left",
    (newAlignment: string) => {
      onChange([
        {
          type: "alignment" as const,
          value: newAlignment as ValidAlignment,
        },
      ]);
    },
  );

  return (
    <div className="alignment-modifier">
      <ToggleGroup
        value={[alignment]}
        onValueChange={(newValue) => {
          if (newValue.length > 0) {
            setAlignment(newValue[0]!);
          }
        }}
        className="w-full"
      >
        <Toggle
          value="left"
          size="sm"
          icon
          aria-label="Align Left"
          className="flex-1"
        >
          <AlignLeft size={16} />
        </Toggle>
        <Toggle
          value="center"
          size="sm"
          icon
          aria-label="Align Center"
          className="flex-1"
        >
          <AlignCenter size={16} />
        </Toggle>
        <Toggle
          value="right"
          size="sm"
          icon
          aria-label="Align Right"
          className="flex-1"
        >
          <AlignRight size={16} />
        </Toggle>
        <Toggle
          value="justify"
          size="sm"
          icon
          aria-label="Justify"
          className="flex-1"
        >
          <AlignJustify size={16} />
        </Toggle>
      </ToggleGroup>
    </div>
  );
};

export default TextAlignmentModifierComponent;
